import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { Box, Button, Typography } from '@mui/material';
import { useFieldValue } from 'components/form/input/NestedField';
import { useCoordinates } from 'util/geolocation';
import { calcCenterListCordenada, poligonList2Str, poligonStr2List } from './UtilCalcCordenadas';

const containerStyle = {
    position: 'relative',
    flex: 1,
};

const defaultFixedCenter = { lat: -32.2291487, lng: -64.8020967 };

export default function MapPoligons({ name, outrosPoligonos, updatePoligono, pontos, captureRef, showCurrentLocation, sx, actionButton = false, AddApontamentoButton, editPermissions = true }) {
    const [infoId, setInfoId] = useState(null);
    const [map, setMap] = useState(null);
    const [startCenter, setStartCenter] = useState(null);

    const [poligonoStr, setPoligonoStr] = useFieldValue(name || "SEM-NOME");
    const [coordinates] = useCoordinates();
    const polygonRef = useRef(null);
    const listenersRef = useRef([]);

    useEffect(() => {
        if (coordinates && !startCenter) {
            setStartCenter(coordinates);
        }
    }, [coordinates, startCenter]);

    const onEdit = useCallback(() => {
        if (polygonRef.current) {
            const nextPath = polygonRef.current.getPath().getArray().map(latLng => ({
                lat: latLng.lat(),
                lng: latLng.lng()
            }));
            setPoligonoStr(poligonList2Str(nextPath));
            updatePoligono();
        }
    }, [setPoligonoStr, updatePoligono]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyAEhmWG2jNKFUcbApO1bg-alG8TPOrDJqQ"
    });

    const onLoad = useCallback(map => {
        setMap(map);
        if (startCenter) {
            map.setCenter(startCenter);
        } else {
            map.setCenter(defaultFixedCenter);
        }

        if (isLoaded && (poligonoStr || outrosPoligonos || pontos)) {
            let listCordenada = [];

            if (poligonoStr) {
                listCordenada = poligonStr2List(poligonoStr);
            }

            if (outrosPoligonos) {
                outrosPoligonos.forEach(p => {
                    if (p.path) {
                        listCordenada = listCordenada.concat(p.path);
                    }
                });
            }

            if (pontos) {
                pontos.forEach(p => {
                    if (p.position) {
                        listCordenada.push(p.position);
                    }
                });
            }

            if (listCordenada.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                listCordenada.forEach(coord => bounds.extend(coord));
                map.fitBounds(bounds);
            }
        }
    }, [startCenter, isLoaded, poligonoStr, outrosPoligonos, pontos]);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    const onLoadPol = useCallback(polygon => {
        polygonRef.current = polygon;
        const path = polygon.getPath();
        listenersRef.current.push(
            path.addListener("set_at", onEdit),
            path.addListener("insert_at", onEdit),
            path.addListener("remove_at", onEdit)
        );
    }, [onEdit]);

    const onClickMap = useCallback(evt => {
        if (name) {
            let list = poligonoStr ? poligonStr2List(poligonoStr) : [];
            let ponto = { lat: evt.latLng.lat(), lng: evt.latLng.lng() };
            list.push(ponto);

            setPoligonoStr(poligonList2Str(list));
            updatePoligono();
        }
    }, [poligonoStr, name, setPoligonoStr, updatePoligono]);

    const onUnmountPol = useCallback(() => {
        listenersRef.current.forEach(lis => lis.remove());
        polygonRef.current = null;
    }, []);

    const removeUltimo = () => {
        let l = poligonoStr ? poligonStr2List(poligonoStr) : [];
        l.pop();
        setPoligonoStr(poligonList2Str(l));
        updatePoligono();
    };

    const limpar = () => {
        setPoligonoStr("");
        updatePoligono();
    };

    const customIconNone = opts => ({
        path: 'M 0,0 ',
        fillColor: '#f00',
        fillOpacity: 1,
        strokeColor: '#000',
        strokeWeight: 2,
        scale: 1,
        ...opts
    });

    const blueDot = {
        fillColor: '#4285F4',
        fillOpacity: 1,
        path: isLoaded ? window.google.maps.SymbolPath.CIRCLE : "",
        scale: 8,
        strokeColor: "#fff",
        strokeWeight: 2,
    };

    return (
        <Box sx={{ backgroundColor: '#000', flex: 1, display: 'flex', flexDirection: "column", height: "100vh", ...sx }}>
            {isLoaded && (
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={startCenter || defaultFixedCenter}
                    zoom={16}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onClick={editPermissions ? onClickMap : null}
                    options={{
                        streetViewControl: false,
                        mapTypeId: "hybrid"
                    }}
                >
                    {showCurrentLocation && coordinates && (
                        <Marker position={coordinates} icon={blueDot} />
                    )}
                    {poligonoStr && (
                        <Polygon
                            editable
                            path={poligonStr2List(poligonoStr)}
                            onMouseUp={onEdit}
                            onDragEnd={onEdit}
                            onLoad={onLoadPol}
                            onUnmount={onUnmountPol}
                            options={{
                                strokeColor: '#006400',
                                fillColor: "rgba(0,255,0,0.5)",
                            }}
                        />
                    )}
                    {(pontos || []).map((p, i) => (
                        <Marker
                            key={"ponto" + i}
                            position={p.position}
                            onMouseOver={() => setInfoId("markerp-" + p.title)}
                            onMouseOut={() => setInfoId(null)}
                        >
                            {infoId === "markerp-" + p.title && (
                                <InfoWindow>
                                    <div>
                                        <h4>{p.title}</h4>
                                        {Object.entries(p.props || {}).map(([key, value], idx) => (
                                            <p key={"info" + idx}><b>{key}</b> {value}</p>
                                        ))}
                                    </div>
                                </InfoWindow>
                            )}
                        </Marker>
                    ))}
                    {(outrosPoligonos || []).map((p, i) => {
                        let mapP = []
                        if (p.props) {
                            for (const key in p.props) {
                                if (Object.hasOwnProperty.call(p.props, key)) {
                                    const value = p.props[key];
                                    mapP.push({ key, value })
                                }
                            }
                        }
                        return (
                            <Marker
                                key={"pontoPoli" + i}
                                icon={customIconNone({})}
                                position={calcCenterListCordenada(p.path)}>
                                {(infoId === "Polygon-" + p.title) &&
                                    <InfoWindow>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontWeight: 'bold' }}>Nome: </Typography>
                                                <Typography>{p.title}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontWeight: 'bold' }}>Área: </Typography>
                                                <Typography>{p.props.Área}</Typography>
                                            </Box>
                                            {AddApontamentoButton &&
                                                <AddApontamentoButton talhao={p.talhao} />
                                            }
                                        </Box>
                                    </InfoWindow>
                                }

                            </Marker>
                        )
                    })}
                    {(outrosPoligonos || []).map((p, i) => (
                        <Polygon
                            key={"outPol" + i}
                            path={p.path}
                            options={{
                                strokeColor: '#7B1906',
                                fillColor: '#7B1906',
                            }}
                            onMouseOver={() => setInfoId("Polygon-" + p.title)}
                            onMouseOut={() => setInfoId(null)}
                        />
                    ))}
                    <div style={{ position: 'absolute', bottom: 9, right: 45, display: "flex", gap: 10, padding: 15 }}>
                        {(name && editPermissions) && (
                            <>
                                <Button style={{ backgroundColor: 'rgba(255,255,255,0.7)', color: "#000" }} onClick={removeUltimo}>Desfazer</Button>
                                <Button style={{ backgroundColor: 'rgba(255,255,255,0.7)', color: "#000" }} onClick={limpar}>Limpar</Button>
                            </>
                        )}
                        {showCurrentLocation && (
                            <Button style={{ backgroundColor: 'rgba(255,255,255,0.7)', color: "#000" }} onClick={() => {
                                if (coordinates) {
                                    map.panTo(coordinates);
                                }
                            }}>
                                Local
                            </Button>
                        )}
                    </div>
                </GoogleMap>
            )}
        </Box>
    );
}
